/**
 * Collections Table Component
 *
 * Displays a table of collections with summary information.
 * Supports sorting and navigation to collection detail pages.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CollectionListItem } from '../../types/admin.js';

interface CollectionsTableProps {
  collections: CollectionListItem[];
  onSort?: (sort: 'name' | 'creator_address' | 'created_at' | 'total_supply' | 'floor_price', order: 'asc' | 'desc') => void;
}

export function CollectionsTable({ collections, onSort }: CollectionsTableProps) {
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<'name' | 'creator_address' | 'created_at' | 'total_supply' | 'floor_price'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: 'name' | 'creator_address' | 'created_at' | 'total_supply' | 'floor_price') => {
    const newOrder = column === sortColumn && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortOrder(newOrder);
    onSort?.(column, newOrder);
  };

  const handleRowClick = (collection: CollectionListItem) => {
    // Use 'all' for null taxon (issuer-wide collections)
    const taxonParam = collection.taxon !== null ? collection.taxon : 'all';
    navigate(`/admin/collections/${collection.creatorAddress}/${taxonParam}`);
  };

  const getSortIcon = (column: string) => {
    if (column !== sortColumn) {
      return '↕';
    }
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatFloorPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `${numPrice.toFixed(2)} XRP`;
  };

  if (collections.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <p className="text-gray-500">No collections found.</p>
        <a
          href="/admin/collections/add"
          className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Create your first collection
        </a>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
              onClick={() => handleSort('name')}
            >
              Collection {getSortIcon('name')}
            </th>
            <th
              scope="col"
              className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
              onClick={() => handleSort('creator_address')}
            >
              Issuer {getSortIcon('creator_address')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Taxon
            </th>
            <th
              scope="col"
              className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
              onClick={() => handleSort('total_supply')}
            >
              Supply {getSortIcon('total_supply')}
            </th>
            <th
              scope="col"
              className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
              onClick={() => handleSort('floor_price')}
            >
              Floor {getSortIcon('floor_price')}
            </th>
            <th
              scope="col"
              className="cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100"
              onClick={() => handleSort('created_at')}
            >
              Created {getSortIcon('created_at')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {collections.map((collection) => (
            <tr
              key={collection.id}
              onClick={() => handleRowClick(collection)}
              className="cursor-pointer transition-colors hover:bg-gray-50"
            >
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  {collection.imageUrl ? (
                    <img
                      src={collection.imageUrl}
                      alt={collection.name}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">NFT</span>
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-900">{collection.name}</div>
                    <div className="text-xs text-gray-500">/{collection.slug}</div>
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm font-mono text-gray-900">
                  {collection.creatorAddress.substring(0, 8)}...
                  {collection.creatorAddress.substring(collection.creatorAddress.length - 4)}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm font-semibold text-gray-900">
                  {collection.taxon !== null ? collection.taxon : (
                    <span className="text-purple-600">All</span>
                  )}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm text-gray-900">{collection.totalSupply.toLocaleString()}</div>
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm text-gray-900">{formatFloorPrice(collection.floorPrice)}</div>
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm text-gray-500">
                  {new Date(collection.createdAt).toLocaleDateString()}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <div className="flex gap-1">
                  {collection.isVerified && (
                    <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                      Verified
                    </span>
                  )}
                  {collection.isTopCommunity && (
                    <span className="inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800">
                      Top
                    </span>
                  )}
                  {!collection.isVerified && !collection.isTopCommunity && (
                    <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
                      Standard
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
